import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Search, Close } from '@mui/icons-material';
import CurrencyTextField, { formatCurrency } from '../components/CurrencyTextField';
import { procurementSummaryFieldsRowSx } from '../components/procurement/ProcurementSummaryBar';
import { serviceService } from '../services/serviceService';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';

const emptyServiceForm = () => ({
  service_code: '',
  description: '',
  unit: 'HR',
  unit_cost: '',
  unit_price: '',
  category: '',
  is_active: true
});

const rowToForm = (row) => ({
  service_code: row.service_code ?? '',
  description: row.service_description ?? row.description ?? '',
  unit: row.unit != null && String(row.unit).trim() !== '' ? String(row.unit) : 'HR',
  unit_cost: row.unit_cost != null ? String(row.unit_cost) : '',
  unit_price: row.unit_price != null ? String(row.unit_price) : '',
  category: row.category ?? '',
  is_active: row.is_active !== false
});

const Services = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState(emptyServiceForm());

  const loadServices = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await serviceService.getAll({ search: searchTerm || undefined, limit: 200, activeOnly: 'true' });
      const list = res.data ?? [];
      setServices(list);
      setSelectedService(list[0] ?? null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveNew = async () => {
    if (!formData.description?.trim()) {
      setError('Description is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await serviceService.create({
        ...formData,
        unit: formData.unit?.trim() || 'HR',
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : 0
      });
      setNewDialogOpen(false);
      setFormData(emptyServiceForm());
      await loadServices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create service');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedService?.id) return;
    if (!formData.description?.trim()) {
      setError('Description is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await serviceService.update(selectedService.id, {
        ...formData,
        unit: formData.unit?.trim() || 'HR',
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null
      });
      setEditDialogOpen(false);
      setSelectedService(null);
      await loadServices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update service');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService?.id) return;
    try {
      setSaving(true);
      setError('');
      await serviceService.delete(selectedService.id);
      setDeleteConfirmOpen(false);
      setSelectedService(null);
      await loadServices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete service');
    } finally {
      setSaving(false);
    }
  };

  const formFields = (
    <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Service Code" value={formData.service_code} onChange={(e) => setFormData((f) => ({ ...f, service_code: e.target.value }))} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Description" multiline rows={2} value={formData.description} onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))} required />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Unit" value={formData.unit} onChange={(e) => setFormData((f) => ({ ...f, unit: e.target.value }))} placeholder="HR" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <CurrencyTextField fullWidth size="small" label="Unit Cost" value={formData.unit_cost} onChange={(e) => setFormData((f) => ({ ...f, unit_cost: e.target.value }))} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <CurrencyTextField fullWidth size="small" label="Unit Price" value={formData.unit_price} onChange={(e) => setFormData((f) => ({ ...f, unit_price: e.target.value }))} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Category" value={formData.category} onChange={(e) => setFormData((f) => ({ ...f, category: e.target.value }))} />
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>Services</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={() => { if (selectedService) { setFormData(rowToForm(selectedService)); setEditDialogOpen(true); } }} disabled={!selectedService}>Edit</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => selectedService && setDeleteConfirmOpen(true)} disabled={!selectedService}>Delete</Button>
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={() => { setFormData(emptyServiceForm()); setNewDialogOpen(true); }}>New</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
          <Box sx={{ ...procurementSummaryFieldsRowSx(), flex: '1 1 auto' }}>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Service Code</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedService ? effectiveServiceCode(selectedService) : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Unit</Typography>
              <Typography variant="body2">{selectedService?.unit ?? 'HR'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Unit Price</Typography>
              <Typography variant="body2">{selectedService ? formatCurrency(selectedService.unit_price) : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Category</Typography>
              <Typography variant="body2">{selectedService?.category ?? '—'}</Typography>
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 160 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Description</Typography>
              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedService?.service_description ?? '—'}</Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setSelectedService(null)} disabled={!selectedService} sx={{ flexShrink: 0 }}><Close fontSize="small" /></IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by code or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadServices()}
            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={loadServices}><Search /></IconButton></InputAdornment> }}
            sx={{ minWidth: 280 }}
          />
          <Button variant="outlined" size="small" onClick={loadServices}>Search</Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Service Code</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Unit Price</TableCell>
              <TableCell>Category</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : services.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary">No services found.</Typography></TableCell></TableRow>
            ) : services.map((row) => (
              <TableRow key={row.id} hover selected={selectedService?.id === row.id} onClick={() => setSelectedService(row)} sx={{ cursor: 'pointer' }}>
                <TableCell>{effectiveServiceCode(row) || '—'}</TableCell>
                <TableCell>{row.service_description ?? '—'}</TableCell>
                <TableCell>{row.unit ?? 'HR'}</TableCell>
                <TableCell>{formatCurrency(row.unit_price)}</TableCell>
                <TableCell>{row.category ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={newDialogOpen} onClose={() => !saving && setNewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Service</DialogTitle>
        <DialogContent>{formFields}</DialogContent>
        <DialogActions><Button onClick={() => setNewDialogOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={handleSaveNew} disabled={saving}>Save</Button></DialogActions>
      </Dialog>
      <Dialog open={editDialogOpen} onClose={() => !saving && setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent>{formFields}</DialogContent>
        <DialogActions><Button onClick={() => setEditDialogOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={handleUpdate} disabled={saving}>Save</Button></DialogActions>
      </Dialog>
      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Service</DialogTitle>
        <DialogContent><Typography>Delete service <strong>{selectedService ? effectiveServiceCode(selectedService) || selectedService?.service_description || selectedService?.id : ''}</strong>?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button><Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>Delete</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;
